import { Drawer } from '@medusajs/ui';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useMemo } from 'react';

import { useCartDrawer } from '@lib/context/cart-drawer-context';
import Button from '@modules/common/components/button';
import Text from '@modules/common/components/text';
import { cartDataQueryOptions } from '@lib/query/options/cart';
import { getPromotionsFromCart } from '@lib/util/get-promotions-from-cart';
import DiscountTemplate from '@modules/cart/templates/discount';

import CartItem from './cart-item';

// Re-export context hook for convenience
export { useCartDrawer } from '@lib/context/cart-drawer-context';
export { CartDrawerProvider } from '@lib/context/cart-drawer-context';

/**
 * Cart Drawer UI Component
 *
 * Displays the shopping cart in a slide-out drawer.
 * Shows cart items, total, and checkout/continue shopping buttons.
 */
export function CartDrawer() {
  const { isOpen, toggleDrawer, closeDrawer, isLoggedIn } = useCartDrawer();

  // Fetch cart data with enriched items - only when logged in and drawer is open
  const { data, refetch, isError, isFetching } = useQuery({
    ...cartDataQueryOptions(),
    enabled: isOpen && isLoggedIn,
  });

  const cart = data?.cart;
  const checkoutThresholdAmount = data?.checkout_threshold_amount ?? 0;
  const freeShippingPromotion = data?.free_shipping_promotion ?? null;

  // Refetch cart data when drawer opens
  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  // Sort items by created_at (oldest first)
  const sortedItems = useMemo(() => {
    const enrichedItems = data?.enriched_items ?? [];
    return [...enrichedItems].sort((a, b) =>
      String(a.created_at ?? '').localeCompare(String(b.created_at ?? ''))
    );
  }, [data?.enriched_items]);

  const itemTotal = cart?.item_total ?? 0;
  const currencyCode = cart?.currency_code ?? 'TWD';
  const hasItems = sortedItems.length > 0;
  const canCheckout = itemTotal >= checkoutThresholdAmount;

  // Extract promotions from cart
  const promotions = useMemo(() => getPromotionsFromCart(cart), [cart]);

  return (
    <Drawer open={isOpen} onOpenChange={toggleDrawer}>
      <Drawer.Content className="!z-50" data-testid="cart-drawer">
        <Drawer.Header>
          <Drawer.Title asChild>
            <Text variant="title-bold">
              購物車
              {hasItems && <span> ( {sortedItems.length} 件商品)</span>}
            </Text>
          </Drawer.Title>
        </Drawer.Header>

        <Drawer.Body className="overflow-auto">
          {isError ? (
            <div className="mt-8 flex flex-col items-center gap-4">
              <p className="text-red-600">載入購物車失敗</p>
              <Button
                onClick={() => refetch()}
                variant="outlined"
                disabled={isFetching}
              >
                {isFetching ? '載入中...' : '重新載入'}
              </Button>
            </div>
          ) : hasItems ? (
            sortedItems.map((item) => (
              <CartItem key={item.id} item={item} onClose={closeDrawer} />
            ))
          ) : (
            <div className="mt-8 flex flex-col items-center gap-8">
              <p className="text-grey-50" data-testid="cart-drawer-empty-message">
                您的購物車尚未有商品
              </p>
              <Button onClick={closeDrawer} fullWidth data-testid="cart-drawer-browse-button">
                馬上去逛逛
              </Button>
            </div>
          )}
        </Drawer.Body>

        {hasItems && (
          <Drawer.Footer className="justify-start p-6">
            <div className="flex w-full flex-col gap-4">
              {/* Discount and free shipping info */}
              <DiscountTemplate
                promotions={promotions}
                itemTotal={itemTotal}
                currencyCode={currencyCode}
                hideTitle
                showShippingHint
                freeshippingPromotion={freeShippingPromotion}
                className="p-0"
              />

              {/* Checkout threshold warning */}
              {!canCheckout && checkoutThresholdAmount > 0 && (
                <p className="text-sm text-orange-600" data-testid="cart-drawer-threshold-warning">
                  訂單金額須滿 NT$ {checkoutThresholdAmount.toLocaleString()}{' '}
                  才可結帳（還差 NT${' '}
                  {(checkoutThresholdAmount - itemTotal).toLocaleString()}）
                </p>
              )}

              {/* Total */}
              <Text variant="h3-bold" as="p" data-testid="cart-drawer-total">
                商品金額 NT${itemTotal || '-'}
              </Text>

              {/* Action buttons */}
              <div className="mt-4 flex flex-col gap-3">
                <Button
                  as="link"
                  href="/checkout?step=1"
                  onClick={closeDrawer}
                  fullWidth
                  className={clsx(
                    'text-center',
                    !canCheckout && 'pointer-events-none opacity-50'
                  )}
                  data-testid="cart-drawer-checkout-button"
                >
                  結帳
                </Button>

                <Button
                  onClick={closeDrawer}
                  variant="outlined"
                  fullWidth
                  data-testid="cart-drawer-continue-button"
                >
                  繼續購物
                </Button>
              </div>
            </div>
          </Drawer.Footer>
        )}
      </Drawer.Content>
    </Drawer>
  );
}

export default CartDrawer;
